import { buildGamePool, generateBoard } from "@thai-bingo/shared";
import { state } from "../state";
import { t } from "../i18n/i18n";
import { ensureAudio } from "../audio/audio";
import { $ } from "./dom";
import { renderGame } from "./boards";
import { setVoiceStatus } from "../game/caller";
import { isAllBots, startAutoPlay } from "../game/bot";
import { generateRandomName } from "../game/names";
import { buildLocalSession, saveSession } from "../session";

export function initSetup(): void {
    // --- Game type toggle ---
    let gtContainer = $("game-type-btns");
    if (!gtContainer) {
        const section = document.createElement("div");
        section.className = "setup-section";
        section.id = "game-type-section";
        const label = document.createElement("label");
        label.setAttribute("data-i18n", "gameType");
        label.textContent = t("gameType");
        section.appendChild(label);
        gtContainer = document.createElement("div");
        gtContainer.className = "btn-group";
        gtContainer.id = "game-type-btns";
        section.appendChild(gtContainer);
        const setupContainer = document.querySelector(".setup-container");
        if (setupContainer) {
            setupContainer.insertBefore(section, setupContainer.querySelector(".setup-section"));
        }
    }
    gtContainer.innerHTML = "";
    ([
        { key: "practice" as const, i18n: "gameTypePractice" as const },
        { key: "together" as const, i18n: "gameTypeTogether" as const },
    ]).forEach(g => {
        const btn = document.createElement("button");
        btn.textContent = t(g.i18n);
        btn.className = g.key === state.gameType ? "active" : "";
        btn.addEventListener("click", () => { state.gameType = g.key; initSetup(); });
        gtContainer!.appendChild(btn);
    });

    // --- Clean up dynamic sections first ---
    const oldOnline = $("online-setup-section");
    if (oldOnline) oldOnline.remove();

    const oldBotCount = $("bot-count-section");
    if (oldBotCount) oldBotCount.remove();

    // --- Show/hide sections based on game type ---
    const startBtn = $("start-game-btn") as HTMLButtonElement | null;
    const modeSection = $("mode-btns")?.closest(".setup-section");

    if (state.gameType === "practice") {
        if (modeSection) modeSection.classList.remove("hidden");
        if (startBtn) {
            startBtn.classList.remove("hidden");
            startBtn.textContent = t("startGame");
        }
        renderBotCountSelector();
    } else {
        if (modeSection) modeSection.classList.add("hidden");
        if (startBtn) startBtn.classList.add("hidden");
        renderOnlineSetup();
    }

    // --- Mode buttons ---
    const modeContainer = $("mode-btns");
    if (modeContainer) {
        modeContainer.innerHTML = "";
        ([
            { key: "consonants", i18n: "modeConsonants" as const },
            { key: "vowels", i18n: "modeVowels" as const },
            { key: "mixed", i18n: "modeMixed" as const },
        ]).forEach(m => {
            const btn = document.createElement("button");
            btn.textContent = t(m.i18n);
            btn.className = m.key === state.mode ? "active" : "";
            btn.addEventListener("click", () => { state.mode = m.key; initSetup(); });
            modeContainer.appendChild(btn);
        });
    }
}

function renderBotCountSelector(): void {
    const container = document.querySelector(".setup-container");
    if (!container) return;

    const section = document.createElement("div");
    section.className = "setup-section";
    section.id = "bot-count-section";
    const label = document.createElement("label");
    label.textContent = t("botCount");
    section.appendChild(label);
    const group = document.createElement("div");
    group.className = "btn-group";

    // Default bot count: use botPlayers length - 1 (index 0 is moderator) or 3
    const currentBotCount = state.botPlayers.length > 1 ? state.botPlayers.length - 1 : 3;

    [1, 2, 3].forEach(n => {
        const btn = document.createElement("button");
        btn.textContent = String(n);
        btn.className = n === currentBotCount ? "active" : "";
        btn.addEventListener("click", () => {
            state.botPlayers = [false, ...Array(n).fill(true) as boolean[]];
            state.playerCount = n + 1;
            initSetup();
        });
        group.appendChild(btn);
    });

    section.appendChild(group);

    // Insert before hint toggle
    const hintsToggle = container.querySelector(".hint-toggle");
    if (hintsToggle) container.insertBefore(section, hintsToggle);
}

export function renderOnlineSetup(): void {
    const container = document.querySelector(".setup-container");
    if (!container) return;
    const section = document.createElement("div");
    section.id = "online-setup-section";

    // Role toggle: Create / Join
    const roleSection = document.createElement("div");
    roleSection.className = "setup-section";
    const roleLabel = document.createElement("label");
    roleLabel.textContent = t("onlineRole");
    roleSection.appendChild(roleLabel);
    const roleGroup = document.createElement("div");
    roleGroup.className = "btn-group";
    ([
        { key: "create" as const, i18n: "createRoom" as const },
        { key: "join" as const, i18n: "joinRoom" as const },
    ]).forEach(r => {
        const btn = document.createElement("button");
        btn.textContent = t(r.i18n);
        btn.className = r.key === state.onlineRole ? "active" : "";
        btn.addEventListener("click", () => { state.onlineRole = r.key; initSetup(); });
        roleGroup.appendChild(btn);
    });
    roleSection.appendChild(roleGroup);
    section.appendChild(roleSection);

    if (state.onlineRole === "create") {
        // Mode selector for create flow
        const modeDiv = document.createElement("div");
        modeDiv.className = "setup-section";
        const modeLabel = document.createElement("label");
        modeLabel.textContent = t("mode");
        modeDiv.appendChild(modeLabel);
        const modeGroup = document.createElement("div");
        modeGroup.className = "btn-group";
        ([
            { key: "consonants", i18n: "modeConsonants" as const },
            { key: "vowels", i18n: "modeVowels" as const },
            { key: "mixed", i18n: "modeMixed" as const },
        ]).forEach(m => {
            const btn = document.createElement("button");
            btn.textContent = t(m.i18n);
            btn.className = m.key === state.mode ? "active" : "";
            btn.addEventListener("click", () => { state.mode = m.key; initSetup(); });
            modeGroup.appendChild(btn);
        });
        modeDiv.appendChild(modeGroup);
        section.appendChild(modeDiv);

        const nameDiv = document.createElement("div");
        nameDiv.className = "online-setup-section";
        const nameLabel = document.createElement("label");
        nameLabel.textContent = t("moderatorName");
        nameDiv.appendChild(nameLabel);
        const nameRow = document.createElement("div");
        nameRow.className = "name-input-row";
        const nameInput = document.createElement("input");
        nameInput.type = "text";
        nameInput.id = "moderator-name";
        nameInput.placeholder = t("moderatorName");
        nameRow.appendChild(nameInput);
        const modDice = document.createElement("button");
        modDice.type = "button";
        modDice.className = "name-randomize";
        modDice.textContent = "\uD83C\uDFB2";
        modDice.title = t("randomName");
        modDice.addEventListener("click", () => {
            nameInput.value = generateRandomName(state.lang);
        });
        nameRow.appendChild(modDice);
        nameDiv.appendChild(nameRow);
        section.appendChild(nameDiv);

        const createBtn = document.createElement("button");
        createBtn.type = "button";
        createBtn.className = "start-button";
        createBtn.textContent = t("createRoomBtn");
        createBtn.addEventListener("click", () => {
            import("../ws/room-api").then(mod => mod.createRoom());
        });
        section.appendChild(createBtn);
    } else {
        const codeDiv = document.createElement("div");
        codeDiv.className = "online-setup-section";
        const codeLabel = document.createElement("label");
        codeLabel.textContent = t("roomCodeLabel");
        codeDiv.appendChild(codeLabel);
        const codeInput = document.createElement("input");
        codeInput.type = "text";
        codeInput.id = "join-room-code";
        codeInput.className = "room-code-input";
        codeInput.placeholder = t("roomCodePlaceholder");
        codeInput.maxLength = 6;
        codeInput.addEventListener("input", () => {
            codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
            codeInput.classList.remove("error");
            const errMsg = codeInput.parentElement?.querySelector(".room-code-error");
            if (errMsg) errMsg.remove();
        });
        codeDiv.appendChild(codeInput);
        section.appendChild(codeDiv);

        // Pre-fill room code from URL (/rooms/CODE or legacy ?room=CODE)
        const prefillCode = state.roomCode
            || new URLSearchParams(location.search).get("room")?.toUpperCase();
        if (prefillCode) {
            setTimeout(() => {
                const el = $("join-room-code") as HTMLInputElement | null;
                if (el && !el.value) el.value = prefillCode;
            }, 0);
        }

        const nameDiv = document.createElement("div");
        nameDiv.className = "online-setup-section";
        const nameLabel = document.createElement("label");
        nameLabel.textContent = t("joinName");
        nameDiv.appendChild(nameLabel);
        const nameRow = document.createElement("div");
        nameRow.className = "name-input-row";
        const nameInput = document.createElement("input");
        nameInput.type = "text";
        nameInput.id = "join-player-name";
        nameInput.placeholder = t("joinName");
        nameRow.appendChild(nameInput);
        const joinDice = document.createElement("button");
        joinDice.type = "button";
        joinDice.className = "name-randomize";
        joinDice.textContent = "\uD83C\uDFB2";
        joinDice.title = t("randomName");
        joinDice.addEventListener("click", () => {
            nameInput.value = generateRandomName(state.lang);
        });
        nameRow.appendChild(joinDice);
        nameDiv.appendChild(nameRow);
        section.appendChild(nameDiv);

        const joinBtn = document.createElement("button");
        joinBtn.type = "button";
        joinBtn.className = "start-button";
        joinBtn.textContent = t("joinBtn");
        joinBtn.addEventListener("click", () => {
            import("../ws/room-api").then(mod => mod.joinRoom());
        });
        section.appendChild(joinBtn);
    }

    const hintsToggle = container.querySelector(".hint-toggle");
    if (hintsToggle) container.insertBefore(section, hintsToggle);
}

export function startGame(): void {
    ensureAudio();

    // Read bot count from selector (default 3)
    const botCount = state.botPlayers.length > 1 ? state.botPlayers.length - 1 : 3;
    state.playerCount = botCount + 1;
    state.botPlayers = [false, ...Array(botCount).fill(true) as boolean[]];

    // Auto-generate all names
    state.players = [];
    state.players.push(generateRandomName(state.lang)); // Moderator
    for (let i = 0; i < botCount; i++) {
        state.players.push(generateRandomName(state.lang));
    }

    state.hintsOn = ($("hints-check") as HTMLInputElement | null)?.checked ?? true;

    state.gamePool = buildGamePool(state.mode);
    state.calledChars = [];
    state.currentChar = null;
    state.pendingChar = null;
    state.pendingSelections = {};
    state.winners = [];
    state.gameActive = true;

    state.boards = [];
    for (let p = 0; p < state.playerCount; p++) {
        state.boards.push(generateBoard(state.gamePool));
    }

    renderGame();
    const setupScreen = $("setup-screen");
    if (setupScreen) setupScreen.classList.add("hidden");
    const gameScreen = $("game-screen");
    if (gameScreen) gameScreen.classList.remove("hidden");
    const winOverlay = $("win-overlay");
    if (winOverlay) winOverlay.classList.add("hidden");

    const randomBtn = $("random-btn") as HTMLButtonElement | null;
    if (randomBtn) randomBtn.disabled = false;
    const replayBtn = $("replay-btn");
    if (replayBtn) replayBtn.classList.add("hidden");
    const revealBtn = $("reveal-btn");
    if (revealBtn) revealBtn.classList.add("hidden");
    setVoiceStatus(t("statusReady"), "", "statusReady");

    history.pushState(null, "", "/rooms/local");
    const session = buildLocalSession(state);
    if (session) saveSession(session);

    if (isAllBots(state.botPlayers)) {
        startAutoPlay();
    }
}

export function showRoomCodeError(input: HTMLInputElement, message: string): void {
    input.classList.add("error");
    // Remove existing error message if any
    const existing = input.parentElement?.querySelector(".room-code-error");
    if (existing) existing.remove();
    const errMsg = document.createElement("div");
    errMsg.className = "room-code-error";
    errMsg.textContent = message;
    input.insertAdjacentElement("afterend", errMsg);
}
